/**
 * Billing platform resolution for the shared Stripe subscription surface.
 *
 * Part of the "Stripe subscription payment integration" epic. The subscription/
 * payment surface is ONE reusable surface shared across all three platforms;
 * plans/prices differ per platform, so every consumer needs to know which
 * platform the current user belongs to. This is the single source of truth for
 * that mapping so the page, nav gating, and (later) the per-platform Plans slice
 * all agree.
 *
 * Mapping mirrors the role/company_type gating already used across the app:
 *   - recruiter → role hr / admin / super_admin (the existing `/billing` users)
 *   - training  → role "training-agent" AND company_type "training-agent"
 *                 (same compound check as src/containers/layout/training-auth.tsx)
 *   - agent     → role "agent"
 */
export type BillingPlatform = "recruiter" | "training" | "agent";

interface BillingPlatformUser {
    role?: string;
    company_type?: string;
}

const RECRUITER_ROLES = ["hr", "admin", "super_admin"];

/**
 * Resolve the current user's billing platform, or `null` when the user is not on
 * any platform that has a subscription surface (e.g. job_seeker, or no user yet).
 */
export function resolveBillingPlatform(
    user: BillingPlatformUser | undefined | null,
): BillingPlatform | null {
    if (!user?.role) return null;

    if (user.role === "training-agent" && user.company_type === "training-agent") {
        return "training";
    }
    if (user.role === "agent") {
        return "agent";
    }
    if (RECRUITER_ROLES.includes(user.role)) {
        return "recruiter";
    }
    return null;
}

/** Human-readable label for a platform — used in the surface header copy. */
export function billingPlatformLabel(platform: BillingPlatform): string {
    switch (platform) {
        case "recruiter":
            return "Recruitment";
        case "training":
            return "Training";
        case "agent":
            return "Agent";
    }
}

/**
 * Whether the shared Stripe subscription surface is enabled. Read here once so
 * the nav entries and the route guard agree. Flag OFF (default / undefined) ⇒
 * zero visual change anywhere; the whole epic ships behind this flag and is
 * switched on once all sibling slices (card collection, subscribe) land.
 */
export const STRIPE_SUBSCRIPTION_ENABLED =
    import.meta.env.VITE_STRIPE_SUBSCRIPTION_ENABLED === "true";
